/**
 * <DocumentUpload /> — upload an employee form (I-9, W-4, etc.).
 *
 * Route: /onboarding/upload/:itemId
 *
 * Supports phone camera capture for ID photos (capture="environment") and
 * file picker upload. Allows re-upload before manager review (overwrites
 * previous upload metadata; storage path includes timestamp so historical
 * objects remain untouched).
 *
 * On failure, the last-chosen File is preserved so the user can retry
 * without re-picking — important on flaky cell connections at job sites.
 * Network vs. validation errors are surfaced distinctly. After success,
 * offers a one-tap "Continue to next item" CTA.
 *
 * @prop {string} itemId
 */

import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Upload, FileText, CheckCircle2, WifiOff, RefreshCw } from 'lucide-react';
import { useToast } from '../../../components/Toast';
import { useAuth } from '../../../context/AuthContext';
import { useApp } from '../../../context/AppContext';
import {
  getChecklistItem,
  uploadChecklistDocument,
  getUploadedDocument,
  listChecklistItems,
} from '../services/onboardingService';
import '../Onboarding.css';

const DEFAULT_MAX_MB = 10;
const DEFAULT_TYPES = ['pdf', 'jpg', 'jpeg', 'png'];

function isOfflineError(err) {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return true;
  if (err && err.name === 'TypeError') return true;
  const msg = String(err?.message || '').toLowerCase();
  return msg.includes('network') || msg.includes('failed to fetch');
}

export default function DocumentUpload() {
  const { itemId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { user } = useAuth();
  const { state } = useApp();

  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dragging, setDragging] = useState(false);
  const [progress, setProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [completedDoc, setCompletedDoc] = useState(null);
  const [pendingFile, setPendingFile] = useState(null);
  const [uploadError, setUploadError] = useState(null); // { offline: bool, message: string }
  const [nextItemPath, setNextItemPath] = useState(null);
  const fileInputRef = useRef(null);
  const confirmHeadingRef = useRef(null);

  const employee = user?.email
    ? state.employees.find((e) => e.email?.toLowerCase() === user.email.toLowerCase())
    : null;

  useEffect(() => {
    let active = true;
    (async () => {
      const it = await getChecklistItem(itemId);
      if (!active) return;
      if (!it) {
        toast.error('Checklist item not found');
        navigate('/onboarding');
        return;
      }
      setItem(it);
      if (it.status === 'completed' && it.metadata?.upload_id) {
        const doc = await getUploadedDocument(it.metadata.upload_id);
        if (doc) setCompletedDoc(doc);
      }
      setLoading(false);
    })();
    return () => { active = false; };
  }, [itemId, navigate, toast]);

  // After success, find the next employee-driven item so the user can
  // continue in one tap instead of going back to the checklist.
  useEffect(() => {
    if (!completedDoc || !employee?.id) return;
    let active = true;
    (async () => {
      try {
        const rows = await listChecklistItems(employee.id);
        if (!active) return;
        const next = rows.find(
          (r) => r.id !== itemId && r.driver !== 'manager' && r.status !== 'completed'
        );
        if (next) {
          const path = next.type === 'esign'
            ? `/onboarding/sign/${next.id}`
            : next.type === 'upload'
              ? `/onboarding/upload/${next.id}`
              : null;
          setNextItemPath(path);
        } else {
          setNextItemPath(null);
        }
      } catch (_) {
        // Non-fatal.
      }
    })();
    return () => { active = false; };
  }, [completedDoc, employee?.id, itemId]);

  useEffect(() => {
    if (completedDoc && confirmHeadingRef.current) {
      confirmHeadingRef.current.focus();
    }
  }, [completedDoc]);

  const acceptedTypes = item?.upload_config?.accepted_types || DEFAULT_TYPES;
  const maxMb = item?.upload_config?.max_mb || DEFAULT_MAX_MB;
  const acceptAttr = acceptedTypes
    .map((t) => (t === 'pdf' ? 'application/pdf' : `image/${t}`))
    .join(',');

  const validateFile = (file) => {
    const ext = file.name.split('.').pop().toLowerCase();
    if (!acceptedTypes.includes(ext)) {
      return `Unsupported file type. Accepted: ${acceptedTypes.join(', ').toUpperCase()}`;
    }
    if (file.size > maxMb * 1024 * 1024) {
      return `File too large. Max ${maxMb} MB.`;
    }
    return null;
  };

  const doUpload = async (file) => {
    if (!employee) {
      toast.error('Employee record not found for current user');
      return;
    }
    const validationMsg = validateFile(file);
    if (validationMsg) {
      setUploadError({ offline: false, message: validationMsg, validation: true });
      // Don't preserve invalid files for retry — user must pick a different one.
      setPendingFile(null);
      toast.error(validationMsg);
      return;
    }

    setPendingFile(file);
    setUploadError(null);
    setUploading(true);
    setProgress(0);
    try {
      const result = await uploadChecklistDocument({
        file,
        employeeId: employee.id,
        checklistItemId: item.id,
        documentType: item.template_item_id || 'other',
        onProgress: setProgress,
      });
      const doc = await getUploadedDocument(result.documentId);
      setCompletedDoc(doc);
      setPendingFile(null);
      toast.success('Upload complete');
    } catch (e) {
      if (isOfflineError(e)) {
        setUploadError({
          offline: true,
          message: 'You appear to be offline. Your upload did not finish.',
        });
      } else {
        setUploadError({
          offline: false,
          message: e.message || 'Upload failed. Please try again.',
        });
      }
    } finally {
      setUploading(false);
    }
  };

  const onFileChosen = (e) => {
    const file = e.target.files?.[0];
    if (file) doUpload(file);
    // Reset input so the same filename can be picked again later.
    e.target.value = '';
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) doUpload(file);
  };

  const onReplaceClick = () => {
    // Keep completedDoc until a new file is actually chosen — protects the
    // success view if the user opens the picker and then cancels.
    fileInputRef.current?.click();
  };

  const onReplaceFileChosen = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return; // user cancelled — keep success view
    setCompletedDoc(null);
    doUpload(file);
  };

  if (loading) {
    return <div className="onboarding-shell"><div className="loading-screen"><div className="spinner-large" /><p>Loading…</p></div></div>;
  }

  return (
    <div className="onboarding-shell">
      <Link to="/onboarding" className="onboarding-btn secondary" style={{ padding: '6px 10px', fontSize: '0.8rem' }}>
        <ArrowLeft size={14} aria-hidden="true" /> Back
      </Link>
      <h1 style={{ fontFamily: 'Fraunces, Georgia, serif', marginTop: 8 }}>{item?.title}</h1>
      {item?.description && <p style={{ color: '#555' }}>{item.description}</p>}
      <p style={{ fontSize: '0.85rem', color: '#777' }}>
        Accepted: {acceptedTypes.join(', ').toUpperCase()} · Max {maxMb} MB
      </p>

      {completedDoc ? (
        <div className="onboarding-celebration">
          <CheckCircle2 size={48} color="#3b6e4a" aria-hidden="true" />
          <h2 ref={confirmHeadingRef} tabIndex={-1} style={{ outline: 'none' }}>Uploaded</h2>
          <p style={{ fontSize: '0.9rem' }}>
            <FileText size={14} aria-hidden="true" /> {completedDoc.file_name}
          </p>
          {completedDoc.file_url && (
            <a className="onboarding-btn secondary" href={completedDoc.file_url} target="_blank" rel="noreferrer" style={{ marginRight: 8 }}>
              View uploaded file
            </a>
          )}
          {!completedDoc.reviewed_at && (
            <>
              <button className="onboarding-btn secondary" onClick={onReplaceClick}>
                Replace upload
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept={acceptAttr}
                capture="environment"
                style={{ display: 'none' }}
                onChange={onReplaceFileChosen}
              />
            </>
          )}
          <div style={{ marginTop: 12 }}>
            {nextItemPath ? (
              <>
                <button className="onboarding-btn" onClick={() => navigate(nextItemPath)}>
                  Continue to next item <ArrowRight size={16} aria-hidden="true" style={{ marginLeft: 6 }} />
                </button>
                <div style={{ marginTop: 8 }}>
                  <button className="onboarding-btn secondary" onClick={() => navigate('/onboarding')}>
                    Back to checklist
                  </button>
                </div>
              </>
            ) : (
              <button className="onboarding-btn secondary" onClick={() => navigate('/onboarding')}>
                Back to checklist
              </button>
            )}
          </div>
        </div>
      ) : (
        <>
          <div
            className={`onboarding-upload-zone ${dragging ? 'dragging' : ''}`}
            onClick={() => fileInputRef.current?.click()}
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={onDrop}
            role="button"
            tabIndex={0}
            aria-label={`Upload ${item?.title || 'document'}`}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                fileInputRef.current?.click();
              }
            }}
          >
            <Upload size={32} color="#3b6e4a" aria-hidden="true" />
            <p
              style={{ marginTop: 8 }}
              role={uploading ? 'progressbar' : undefined}
              aria-valuenow={uploading ? progress : undefined}
              aria-valuemin={uploading ? 0 : undefined}
              aria-valuemax={uploading ? 100 : undefined}
              aria-live="polite"
            >
              {uploading
                ? `Uploading… ${progress}%`
                : 'Tap to upload, or drag a file here'}
            </p>
            <input
              ref={fileInputRef}
              type="file"
              accept={acceptAttr}
              capture="environment"
              style={{ display: 'none' }}
              onChange={onFileChosen}
            />
          </div>

          {uploadError && (
            <div className="onboarding-retry-banner" role="alert">
              {uploadError.offline && <WifiOff size={18} aria-hidden="true" />}
              <div style={{ flex: 1 }}>{uploadError.message}</div>
              {pendingFile && !uploadError.validation && (
                <button
                  type="button"
                  className="onboarding-btn"
                  onClick={() => doUpload(pendingFile)}
                  disabled={uploading}
                >
                  <RefreshCw size={14} aria-hidden="true" style={{ marginRight: 6 }} />
                  {uploading ? 'Retrying…' : 'Retry'}
                </button>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
